import './ProductAdditional.scss';

import cn from 'classnames';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import Button from '@/common/components/atoms/Button/Button';
import NavLinkExtended from '@/common/components/blocks/NavLinkExtended/NavLinkExtended';
import { ROUTE_PRODUCT, SOURCE } from '@/common/constants';

import LogoHager from '../Assets/LogoHager/LogoHager';
import {
  ProductAdditionalDefaultProps,
  ProductAdditionalPropTypes,
} from './ProductAdditional.proptypes';

const ProductAdditional = ({
  agrementFgaz,
  bundleParents,
  cartSource,
  className,
  disabled,
  discountClass,
  discountClasses,
  discountLabel,
  id,
  isLoading,
  isNotSalable,
  isSubstitute,
  onRemove,
  quoteNumber,
  substitute,
  source,
}) => {
  const { t } = useTranslation();

  const isDisabled = useMemo(() => {
    const blocked = isLoading || disabled;

    return {
      disabled: blocked,
      loading: isLoading,
    };
  }, [disabled, isLoading]);

  const getDiscountLabel = useMemo(() => {
    const item = discountClasses.find((dc) => dc.code === discountClass);

    if (discountLabel) {
      return discountLabel;
    }

    if (item) {
      return item.label;
    }

    return discountClass;
  }, [discountClass, discountClasses, discountLabel]);

  if (
    quoteNumber ||
    agrementFgaz ||
    bundleParents.length > 0 ||
    discountClass ||
    isNotSalable ||
    source === SOURCE.HAGER ||
    (substitute && isSubstitute)
  ) {
    return (
      <ul
        className={cn('product-additional', className)}
        data-qa-id="product-additional"
      >
        {source === SOURCE.HAGER && cartSource === SOURCE.HAGER && (
          <li className="product-additional__item hager" data-qa-id="hager">
            {t('ebat:entities:cart.items.hager')}{' '}
            <LogoHager className="site-logo-hager" />
          </li>
        )}
        {quoteNumber && (
          <li className="product-additional__item" data-qa-id="quote-number">
            {t('ebat:entities:cart.items.quote_number', {
              number: quoteNumber,
            })}
          </li>
        )}
        {agrementFgaz && (
          <li
            className="product-additional__item"
            data-qa-id="mandatory-approval"
          >
            {t('ebat:entities:cart.items.mandatory_approval', {
              approval: agrementFgaz,
            })}
          </li>
        )}
        {bundleParents.length > 0 && (
          <li
            className="product-additional__item bundle-parents"
            data-qa-id="bundle-parents"
          >
            <ul className="list list--bundle-parents">
              {bundleParents.map((item) => (
                <li
                  className="list__item"
                  key={`bundle-parent-${item.path}-${item.slug}`}
                  data-qa-id="bundle-parents-item"
                >
                  <NavLinkExtended
                    className="link"
                    disabled={isDisabled.disabled}
                    to={`${ROUTE_PRODUCT}/${item.path}/${item.slug}`}
                  >
                    {t('common:messages:actions.see_offer')}
                  </NavLinkExtended>
                </li>
              ))}
            </ul>
          </li>
        )}

        {discountClass && (
          <li
            className="product-additional__item discount-class"
            data-qa-id="discount-class"
          >
            {getDiscountLabel}
          </li>
        )}
        {isNotSalable && (
          <li
            className={cn('product-additional__item must-substitute', {
              'no-onremove': !onRemove,
            })}
            data-qa-id="no-salable"
          >
            <span className="label">
              {t('ebat:entities:cart.items.deletion_product')}
            </span>
            {onRemove && (
              <Button
                className="action"
                disabled={isDisabled.disabled}
                onClick={() => onRemove({ id })}
                context="no-salable-remove"
              >
                {t('common:messages:actions.delete')}
              </Button>
            )}
          </li>
        )}
        {substitute && isSubstitute && (
          <li className="product-additional__item" data-qa-id="substitute">
            {t('ebat:entities:cart.items.product_substitute', {
              reference: substitute,
            })}
          </li>
        )}
      </ul>
    );
  }

  return null;
};

ProductAdditional.defaultProps = {
  ...ProductAdditionalDefaultProps,
};

ProductAdditional.propTypes = {
  ...ProductAdditionalPropTypes,
};

export default ProductAdditional;
